import { Button, DropdownMenu, DropdownMenuCheckIcon, DropdownMenuSection } from '@components'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FaFilter } from 'react-icons/fa'
import { WorkflowSortDirection, WorkflowSortField, WorkflowSortState } from './workflowSortUtils'

interface Props {
  sort: WorkflowSortState
  onSortChange: (sort: WorkflowSortState) => void
}

const FIELD_OPTIONS: WorkflowSortField[] = ['updated_at', 'created_at', 'name']

export const WorkflowSortMenu: React.FC<Props> = ({ sort, onSortChange }) => {
  const [t] = useTranslation()

  const fieldLabel = useMemo(() => {
    switch (sort.field) {
      case 'name':
        return t('workflows.sort.byName')
      case 'created_at':
        return t('workflows.sort.byCreated')
      default:
        return t('workflows.sort.byUpdated')
    }
  }, [sort.field, t])

  const orderLabel = useMemo(() => {
    if (sort.field === 'name') {
      return sort.direction === 'asc' ? t('workflows.sort.aToZ') : t('workflows.sort.zToA')
    }

    return sort.direction === 'desc'
      ? t('workflows.sort.newestFirst')
      : t('workflows.sort.oldestFirst')
  }, [sort.direction, sort.field, t])

  const orderOptions = useMemo((): { value: WorkflowSortDirection; label: string }[] => {
    if (sort.field === 'name') {
      return [
        { value: 'asc', label: t('workflows.sort.aToZ') },
        { value: 'desc', label: t('workflows.sort.zToA') }
      ]
    }

    return [
      { value: 'desc', label: t('workflows.sort.newestFirst') },
      { value: 'asc', label: t('workflows.sort.oldestFirst') }
    ]
  }, [sort.field, t])

  const fieldLabelKey = (field: WorkflowSortField): string => {
    switch (field) {
      case 'name':
        return 'workflows.sort.byName'
      case 'created_at':
        return 'workflows.sort.byCreated'
      default:
        return 'workflows.sort.byUpdated'
    }
  }

  return (
    <DropdownMenu
      placement="bottom-start"
      closeOnItemClick={false}
      items={[
        <DropdownMenuSection key="field" label={t('workflows.sort.groupField')}>
          {FIELD_OPTIONS.map(field => (
            <Button
              key={field}
              type="button"
              icon={<DropdownMenuCheckIcon selected={sort.field === field} />}
              onClick={() => onSortChange({ ...sort, field })}
            >
              {t(fieldLabelKey(field))}
            </Button>
          ))}
        </DropdownMenuSection>,
        <DropdownMenuSection key="order" label={t('workflows.sort.groupOrder')}>
          {orderOptions.map(option => (
            <Button
              key={option.value}
              type="button"
              icon={<DropdownMenuCheckIcon selected={sort.direction === option.value} />}
              onClick={() => onSortChange({ ...sort, direction: option.value })}
            >
              {option.label}
            </Button>
          ))}
        </DropdownMenuSection>
      ]}
    >
      <Button
        type="button"
        isSecondary
        size="sm"
        icon={<FaFilter />}
        aria-label={`${t('workflows.sort.button')}: ${fieldLabel}, ${orderLabel}`}
      >
        {t('workflows.sort.button')}
      </Button>
    </DropdownMenu>
  )
}
